import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { GeoJsonProperties, Geometry, Feature } from 'geojson';
import { useNavigate, useParams } from 'react-router-dom';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import CircularProgress from '@mui/material/CircularProgress';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import moment from 'moment-timezone';
import GenericIcons from '../../components/generics/GenericIcons';
import LineChart from '../../components/LineChart';
import EstudioConexionSnapshotBoxplots, {
  ecgExtractBoxplotSnapshot
} from '../../components/estudioConexion/EstudioConexionSnapshotBoxplots';
import EstudioConexionTensionDiariaChart from '../../components/estudioConexion/EstudioConexionTensionDiariaChart';
import EstudioConexionResumenProyectoCard from '../../components/estudioConexion/EstudioConexionResumenProyectoCard';
import MapaImpactoMitigacion from '../../components/estudioConexion/MapaImpactoMitigacion';
import MapComponent from '../../components/maps/Map';
import { SimpleObject } from '../../types';
import { getUncatchEndpointData, sendFileWithData } from '../../utils/request';
import {
  BadgeAdicional,
  BloqueCongestionTransmisionMulti,
  BloqueDeltaVPcMulti,
  BloqueDetallePerdidasMitigacion,
  BloqueVeredictoDual,
  EcgBandasTension,
  analisisDiariosLista,
  analisisPorEscenario,
  analisisSnapshotsLista,
  celdaDeltaSignoClassExport,
  escenariosDiariosConProyecto,
  escenariosSnapshotConProyecto,
  extractBandasTensionArtefacto,
  parseCortocircuitoResumen,
  parsePerdidasArtefacto,
  parseResultadoTecnico,
  perdidasCumpleMitigacion,
  perdidasCumpleProyecto,
  tieneImpactoPorCriterioEnLista
} from '../../components/estudioConexion/estudioConexionResultadoTecnicoShared';
import '../../css/PrivateEstudioConexion.css';

const POLL_INTERVAL = 180000;


const isTerminal = (estadoId: number | null) => estadoId === 2 || estadoId === 3;

const MT_ACTIVE = '#e06c00';
const MT_INACTIVE = '#f0c8a0';
const BT_ACTIVE = '#0072b2';
const BT_INACTIVE = '#a0cfe0';
const UNKNOWN_COLOR = '#c8c8c8';

function numO(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function fmtPct(n: number | null): string {
  if (n === null) return '—';
  return `${n.toFixed(2)}%`;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function etiquetaMitigacion(m: SimpleObject): string {
  const tipo = String(m.tipoMitigacion || m.tipo_mitigacion || '');
  const orden = m.orden != null ? Number(m.orden) + 1 : '';
  return `${tipo.replace(/_/g, ' ')}${orden ? ` (#${orden})` : ''}`;
}

// ─── Componente principal ──────────────────────────────────────────────────

const PrivateEstudioConexionMitigacionResultPage: FunctionComponent = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [estudio, setEstudio] = useState<SimpleObject | null>(null);
  const [resultadosBase, setResultadosBase] = useState<SimpleObject | null>(null);
  const [comparacion, setComparacion] = useState<SimpleObject | null>(null);
  const [mitigaciones, setMitigaciones] = useState<SimpleObject[]>([]);
  const [loading, setLoading] = useState(true);

  // Map state (replicado de PrivateEstudioConexionResultPage)
  const [loadingMap, setLoadingMap] = useState(false);
  const [mapState, setMapState] = useState({
    mapPointFeatures: [] as Feature<Geometry, GeoJsonProperties>[],
    mapLineFeatures: [] as Feature<Geometry, GeoJsonProperties>[],
    mapPolygonFeatures: [] as Feature<Geometry, GeoJsonProperties>[],
    electricFeeders: {} as SimpleObject,
    mapCenter: {} as { lat: number; lng: number }
  });

  // Gráfico comparativo lado a lado
  const [simulacionGrafico, setSimulacionGrafico] = useState('');
  const [casoIzq, setCasoIzq] = useState<CasoGrafico>({ tipo: 'sin_proyecto' });
  const [casoDer, setCasoDer] = useState<CasoGrafico>({ tipo: 'con_proyecto' });
  const [detalleIzq, setDetalleIzq] = useState<SimpleObject[] | null>(null);
  const [detalleDer, setDetalleDer] = useState<SimpleObject[] | null>(null);

  // Desglose state
  const [desgloseTecnicoTab, setDesgloseTecnicoTab] = useState(0);
  const [escenarioDiario, setEscenarioDiario] = useState('');
  const [escenarioSnapshot, setEscenarioSnapshot] = useState('');
  const [selectedMitsDesglose, setSelectedMitsDesglose] = useState<string[]>([]);
  const [selectedMitPerdidas, setSelectedMitPerdidas] = useState<string>('');

  // ─── Data fetching ───────────────────────────────────────────────────────

  const fetchEstudio = useCallback(async () => {
    try {
      const data = await getUncatchEndpointData({ endpoint: `estudios-conexion/${id}` });
      setEstudio(data);
      return data;
    } catch {
      return null;
    }
  }, [id]);

  const fetchAllResults = useCallback(async () => {
    const promises = [
      getUncatchEndpointData({ endpoint: `estudios-conexion/${id}/resultados-base` }).catch(() => null),
      getUncatchEndpointData({ endpoint: `estudios-conexion/${id}/comparacion` }).catch(() => null),
      getUncatchEndpointData({ endpoint: `estudios-conexion/${id}/mitigaciones` }).catch(() => [])
    ] as const;
    const [base, comp, mits] = await Promise.all(promises);
    setResultadosBase(base as SimpleObject | null);
    setComparacion(comp as SimpleObject | null);
    setMitigaciones((mits as SimpleObject[]) || []);
  }, [id]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const est = await fetchEstudio();
      if (est && isTerminal(est.estadoMitigacionId)) {
        await fetchAllResults();
      }
      setLoading(false);
    };
    init();
  }, [fetchEstudio, fetchAllResults]);

  // Polling
  useEffect(() => {
    if (!estudio || isTerminal(estudio.estadoMitigacionId)) return;
    if (!estudio.estadoMitigacionId) return;

    const interval = setInterval(async () => {
      const est = await fetchEstudio();
      if (est && isTerminal(est.estadoMitigacionId)) {
        await fetchAllResults();
      }
    }, POLL_INTERVAL);

    const handleVisibility = async () => {
      if (document.visibilityState === 'visible') {
        const est = await fetchEstudio();
        if (est && isTerminal(est.estadoMitigacionId)) {
          await fetchAllResults();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estudio?.estadoMitigacionId, fetchEstudio, fetchAllResults]);

  // ─── Map (idéntico a PrivateEstudioConexionResultPage) ─────────────────

  const fetchMapPoints = useCallback(
    async (alimentadorId: number, posteId: number | null, nivel: string | null) => {
      if (!alimentadorId) return;
      setLoadingMap(true);
      try {
        const res = await getUncatchEndpointData({
          endpoint: 'mapElements/geoJSON/all',
          query: { alimentadorId }
        });
        const pointFeatures = (res?.pointFeatures || []) as Feature<Geometry, GeoJsonProperties>[];
        const lineFeatures = (res?.lineFeatures || []) as Feature<Geometry, GeoJsonProperties>[];
        const polygonFeatures = (res?.polygonFeatures || []) as Feature<Geometry, GeoJsonProperties>[];
        const alimentadores = res?.alimentadores || {};

        let center = { lat: 0, lng: 0 };
        const selectedPoint = pointFeatures.find((feature) => {
          const featureId = Number(feature.properties?.id ?? feature.properties?.poste_id);
          return posteId != null && !Number.isNaN(featureId) && featureId === posteId;
        });

        if (selectedPoint && selectedPoint.geometry?.type === 'Point') {
          const [lng, lat] = selectedPoint.geometry.coordinates;
          center = { lat, lng };
        } else if (pointFeatures.length > 1 && pointFeatures[1].geometry?.type === 'Point') {
          center = {
            lng: pointFeatures[1].geometry.coordinates[0],
            lat: pointFeatures[1].geometry.coordinates[1]
          };
        } else if (lineFeatures.length > 1 && lineFeatures[1].geometry?.type === 'LineString') {
          center = {
            lng: lineFeatures[1].geometry.coordinates[0][0],
            lat: lineFeatures[1].geometry.coordinates[0][1]
          };
        }

        const styledLines = lineFeatures.map((feature) => {
          const tensionKv = feature.properties?.descripcionArtefacto?.tension_kv;
          if (tensionKv == null) {
            return { ...feature, properties: { ...feature.properties, color: UNKNOWN_COLOR, lineWidth: 1.5 } };
          }
          const isMt = tensionKv > 1;
          const selected = nivel === 'mt' ? isMt : nivel === 'bt' ? !isMt : true;
          const color = isMt
            ? selected ? MT_ACTIVE : MT_INACTIVE
            : selected ? BT_ACTIVE : BT_INACTIVE;
          return {
            ...feature,
            properties: { ...feature.properties, color, lineWidth: selected ? 4 : 2 }
          };
        });

        const styledPoints = pointFeatures.map((feature) => {
          const featureId = Number(feature.properties?.id ?? feature.properties?.poste_id);
          const isSelected = posteId != null && !Number.isNaN(featureId) && featureId === posteId;
          if (!isSelected) return feature;
          return { ...feature, properties: { ...feature.properties, highlighted: true } };
        });

        setMapState({
          mapPointFeatures: styledPoints,
          mapLineFeatures: styledLines,
          mapPolygonFeatures: polygonFeatures,
          electricFeeders: alimentadores,
          mapCenter: center
        });
      } catch (error) {
        console.error(error);
      }
      setLoadingMap(false);
    },
    []
  );

  useEffect(() => {
    if (!estudio?.alimentadorId) return;
    fetchMapPoints(
      Number(estudio.alimentadorId),
      estudio.posteId != null ? Number(estudio.posteId) : null,
      (estudio.nivelTension as string) || null
    );
  }, [estudio?.alimentadorId, estudio?.posteId, estudio?.nivelTension, fetchMapPoints]);

  // ─── Gráfico comparativo: fetch por panel ──────────────────────────

  useEffect(() => {
    if (!simulacionGrafico || !id) { setDetalleIzq(null); return; }
    const sim = SIMULACIONES_GRAFICO.find((s) => s.id === simulacionGrafico);
    if (!sim) { setDetalleIzq(null); return; }
    const params = buildFetchParams(sim, casoIzq);
    if (!params) { setDetalleIzq(null); return; }
    const doFetch = async () => {
      try {
        const data = await getUncatchEndpointData({
          endpoint: `estudios-conexion/${id}/detalle/${params.escenario}`,
          query: params.mitigacionId ? { mitigacionId: params.mitigacionId } : undefined
        });
        setDetalleIzq(data);
      } catch { setDetalleIzq(null); }
    };
    doFetch();
  }, [id, simulacionGrafico, casoIzq]);

  useEffect(() => {
    if (!simulacionGrafico || !id) { setDetalleDer(null); return; }
    const sim = SIMULACIONES_GRAFICO.find((s) => s.id === simulacionGrafico);
    if (!sim) { setDetalleDer(null); return; }
    const params = buildFetchParams(sim, casoDer);
    if (!params) { setDetalleDer(null); return; }
    const doFetch = async () => {
      try {
        const data = await getUncatchEndpointData({
          endpoint: `estudios-conexion/${id}/detalle/${params.escenario}`,
          query: params.mitigacionId ? { mitigacionId: params.mitigacionId } : undefined
        });
        setDetalleDer(data);
      } catch { setDetalleDer(null); }
    };
    doFetch();
  }, [id, simulacionGrafico, casoDer]);

  // ─── Derived data ────────────────────────────────────────────────────

  const resultadoTecnicoData = resultadosBase?.resultadoJson as SimpleObject | undefined;
  const bandasTension = extractBandasTensionArtefacto(resultadoTecnicoData);

  // Auto-select defaults for desglose
  useEffect(() => {
    if (!resultadoTecnicoData) return;
    const rt = parseResultadoTecnico(resultadoTecnicoData);
    const diarios = escenariosDiariosConProyecto(rt);
    const snaps = escenariosSnapshotConProyecto(rt);
    if (diarios.length > 0 && !diarios.some((d) => d.value === escenarioDiario)) {
      setEscenarioDiario(diarios[0].value);
    }
    if (snaps.length > 0 && !snaps.some((d) => d.value === escenarioSnapshot)) {
      setEscenarioSnapshot(snaps[0].value);
    }
  }, [resultadoTecnicoData, escenarioDiario, escenarioSnapshot]);

  // ─── PDF ─────────────────────────────────────────────────────────────

  const exportPDF = async () => {
    const el = document.getElementById('ecg-mit-result-content');
    if (!el) return;
    const canvas = await html2canvas(el, { scale: 2, useCORS: true });
    const pdf = new jsPDF('p', 'mm', 'a4');
    pdf.setFillColor('#f9f9f9');
    pdf.rect(0, 0, 210, 297, 'F');
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, 10, 190, 277);
    const fileName = `Mitigacion_ConexionGD_${estudio?.nombre || id}_${moment().format('DD-MM-YYYY')}.pdf`;
    const pdfBase64 = pdf.output('datauristring');
    try {
      await sendFileWithData({
        endpoint: `estudios-conexion/${id}/exportar-pdf`,
        dataObject: { fileName, fileContent: pdfBase64 }
      });
    } catch (err) {
      console.error(err);
    }
    pdf.save(fileName);
  };

  // ─── Render: guardas ─────────────────────────────────────────────────

  if (loading) {
    return (
      <div className='ecg-page-container' style={{ alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </div>
    );
  }

  if (!estudio) {
    return (
      <div className='ecg-blocked-container'>
        <h2 className='ecg-blocked-title'>Estudio no encontrado</h2>
        <button className='secondary-button-style vdr-ecg-back' onClick={() => navigate('/private/estudios')}>
          Volver a Mis estudios
        </button>
      </div>
    );
  }

  if (estudio.estadoSimulacionId !== 2) {
    return (
      <div className='ecg-blocked-container'>
        <h2 className='ecg-blocked-title'>Resultados de mitigación no disponibles</h2>
        <p className='ecg-blocked-text'>
          El estudio base debe estar finalizado para acceder a los resultados de mitigación.
        </p>
        <div className='vdr-ecg-blocked-actions'>
          <button
            className='primary-button-style vdr-ecg-cta'
            onClick={() => navigate(`/private/estudio-conexion/${id}/resultado`)}>
            Ir a resultados del estudio base
          </button>
        </div>
      </div>
    );
  }

  if (!estudio.estadoMitigacionId) {
    return (
      <div className='ecg-blocked-container'>
        <h2 className='ecg-blocked-title'>Resultados de mitigación no disponibles</h2>
        <p className='ecg-blocked-text'>
          Aún no se han generado resultados para la fase de mitigación.
          Cuando lo desee, puede configurar y ejecutar la evaluación desde la vista correspondiente.
        </p>
        <div className='vdr-ecg-blocked-actions'>
          <button
            className='primary-button-style vdr-ecg-cta'
            onClick={() => navigate(`/private/estudio-conexion/${id}/mitigaciones`)}>
            Configurar mitigaciones
          </button>
          <button
            className='secondary-button-style'
            onClick={() => navigate(`/private/estudio-conexion/${id}/resultado`)}>
            Ir a resultados del estudio base
          </button>
        </div>
      </div>
    );
  }

  const mitEstadoId = estudio.estadoMitigacionId;
  const mitEstadoTexto = estudio.estadoMitigacionTexto || estudio.estadoMitigacion || '';

  // ─── Render: página principal ────────────────────────────────────────

  return (
    <div className='ecg-page-container' id='ecg-mit-result-content'>
      {/* Título + acciones */}
      <div className='ecg-title-container vdr-page-head'>
        <div className='vdr-ecg-heading vdr-page-head__titles'>
          <span className='vdr-eyebrow vdr-eyebrow--bar'>
            Estudio de conexión GD · Mitigaciones · N.º {id}
          </span>
          <h2 className='ecg-title vdr-page-head__title'>Resultados de mitigación — {estudio.nombre}</h2>
        </div>
        <div className='vdr-ecg-actions vdr-page-head__actions'>
          <button className='primary-button-style hidden-to-export' onClick={exportPDF}>
            <GenericIcons icon='filePdfIcon' imageStyle={{ height: '1rem' }} />
            Exportar PDF
          </button>
          <button
            className='secondary-button-style hidden-to-export'
            onClick={() => navigate(`/private/estudio-conexion/${id}/resultado`)}>
            Ver resultados base
          </button>
          <button
            className='secondary-button-style vdr-ecg-back hidden-to-export'
            onClick={() => navigate('/private/estudios')}>
            Volver
          </button>
        </div>
      </div>

      {/* ── Sección 1: Resumen Fase 1 con estado = mitigación ── */}
      <EstudioConexionResumenProyectoCard estudio={estudio} showEstadoMitigacion />

      {/* ── Sección 2: Mapa (idéntico a Fase 1) ── */}
      <div className='ecg-section'>
        <h3 className='ecg-section-title'>Ubicación del Proyecto</h3>
        <p className='vdr-ecg-lead'>
          El mapa muestra el poste y punto de conexión seleccionado para este estudio.
        </p>
        <div className='ecg-map-container' style={{ height: 460 }}>
          <MapComponent
            mapPointFeatures={mapState.mapPointFeatures}
            mapLineFeatures={mapState.mapLineFeatures}
            mapPolygonFeatures={mapState.mapPolygonFeatures}
            electricFeeders={mapState.electricFeeders}
            mapCenter={mapState.mapCenter}
            mapZoom={16.5}
          />
          {loadingMap && (
            <div className='ecg-map-loader'>
              <CircularProgress />
            </div>
          )}
        </div>
      </div>

      {/* ── Sección 3: Configuración Fase 2 (solo lectura) ── */}
      <SeccionConfigFase2 mitigaciones={mitigaciones} />

      {/* ── Estado no terminal: en progreso / en espera ── */}
      {!isTerminal(mitEstadoId) && (
        <div className='ecg-section vdr-ecg-status-panel'>
          <CircularProgress />
          <p className='vdr-ecg-status-text'>
            La evaluación de mitigaciones se encuentra <strong>{mitEstadoTexto?.toLowerCase()}</strong>.
            Esta página se actualiza automáticamente cada 3 minutos.
            Recibirá un correo cuando finalice.
          </p>
        </div>
      )}

      {/* ── Error con CTA reintentar (§5.2 / G-R9) ── */}
      {mitEstadoId === 3 && (
        <div className='ecg-section vdr-ecg-status-panel'>
          <p className='vdr-ecg-status-error'>
            La evaluación de mitigaciones finalizó con un error.
            Si el problema persiste, contacte al administrador.
          </p>
          <button
            className='primary-button-style vdr-ecg-cta'
            onClick={() => navigate(`/private/estudio-conexion/${id}/mitigaciones`)}>
            Reintentar configuración de mitigaciones
          </button>
        </div>
      )}

      {/* ═══════ BLOQUES DE RESULTADOS (solo si mitEstadoId === 2) ═══════ */}
      {mitEstadoId === 2 && (
        <>
          {/* ── Bloque 1: Cumplimiento y costos (NORM-018 A §F.2) ── */}
          <div className='ecg-section'>
            <h3 className='ecg-section-title'>Resumen de cumplimiento y costos</h3>
            <p className='vdr-ecg-lead'>
              Panorama ejecutivo del estudio base y efecto de cada medida. Las filas normativas
              determinan el cumplimiento del proyecto; las filas <em>Adicional</em> son información de
              valor agregado del producto y no definen la decisión de conexión. Las medidas
              operacionales no implican inversión significativa.
            </p>

            {resultadoTecnicoData && (
              <div style={{ marginBottom: '1.25rem' }}>
                <BloqueVeredictoDual data={resultadoTecnicoData} modoMitigacion />
              </div>
            )}

            <BloqueTablaResumenMitigacion
              resultadoTecnicoData={resultadoTecnicoData}
              comparacion={comparacion}
              mitigaciones={mitigaciones}
            />
          </div>

          {/* ── Bloque 2: Desglose por simulación (NORM-018 A §F.3) ── */}
          {resultadoTecnicoData && (
            <div className='ecg-section'>
              <h3 className='ecg-section-title'>Desglose por simulación</h3>
              <p className='vdr-ecg-lead'>
                Profundización técnica por escenario. Mismo patrón de lectura que en Fase 1:
                snapshots (normativo), pérdidas 24×96 (normativo cuando aplica) y flujo horario
                diario (información adicional).
              </p>
              <BloqueDesgloseSimulacion
                resultadoTecnicoData={resultadoTecnicoData}
                comparacion={comparacion}
                mitigaciones={mitigaciones}
                tecnicoTab={desgloseTecnicoTab}
                setTecnicoTab={setDesgloseTecnicoTab}
                escenarioDiario={escenarioDiario}
                setEscenarioDiario={setEscenarioDiario}
                escenarioSnapshot={escenarioSnapshot}
                setEscenarioSnapshot={setEscenarioSnapshot}
                selectedMitsDesglose={selectedMitsDesglose}
                setSelectedMitsDesglose={setSelectedMitsDesglose}
                selectedMitPerdidas={selectedMitPerdidas}
                setSelectedMitPerdidas={setSelectedMitPerdidas}
              />
            </div>
          )}

          {/* ── Bloque 3: Análisis gráfico comparativo ── */}
          <div className='ecg-section'>
            <h3 className='ecg-section-title'>Análisis gráfico por escenario</h3>
            <p className='vdr-ecg-lead'>
              Seleccione una simulación y los casos a comparar lado a lado (panel izquierdo y derecho).
            </p>
            <BloqueGraficoComparativo
              mitigaciones={mitigaciones}
              simulacion={simulacionGrafico}
              setSimulacion={setSimulacionGrafico}
              casoIzq={casoIzq}
              setCasoIzq={setCasoIzq}
              casoDer={casoDer}
              setCasoDer={setCasoDer}
              detalleIzq={detalleIzq}
              detalleDer={detalleDer}
              bandasTension={bandasTension}
            />
          </div>

          {/* ── Bloque 4: Mapa de impacto del proyecto (NORM-037) ── */}
          {mitigaciones.length > 0 && id && (
            <div className='ecg-section'>
              <h3 className='ecg-section-title'>Mapa de impacto del proyecto</h3>
              <p className='vdr-ecg-lead'>
                Compare el impacto del caso con proyecto (sin mitigación) y de cada mitigación
                respecto del caso sin proyecto. Cada combinación de caso y escenario muestra los
                elementos cuyos problemas (sobretensión / sobrecarga) persisten o aparecen frente
                al caso base.
              </p>
              <MapaImpactoMitigacion
                estudioId={id}
                mitigaciones={mitigaciones}
                mapBaseState={mapState}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PrivateEstudioConexionMitigacionResultPage;

// ═══════════════════════════════════════════════════════════════════════════
// ─── Sección 3: Configuración Fase 2 (solo lectura) ─────────────────────
// ═══════════════════════════════════════════════════════════════════════════

const SeccionConfigFase2: FunctionComponent<{ mitigaciones: SimpleObject[] }> = ({ mitigaciones }) => {
  if (mitigaciones.length === 0) return null;

  return (
    <div className='ecg-section'>
      <h3 className='ecg-section-title'>Escenarios de mitigación evaluados</h3>
      <p className='vdr-ecg-lead'>
        Detalle completo de la configuración enviada al motor para cada escenario.
      </p>
      {mitigaciones.map((m, idx) => {
        const tipo = String(m.tipoMitigacion || m.tipo_mitigacion || '');
        const clasif = String(m.clasificacion || '');
        const orden = m.orden != null ? Number(m.orden) + 1 : idx + 1;
        const params = m.parametrosJson || m.parametros_json;
        const medidas: SimpleObject[] =
          tipo === 'combinacion' && params?.medidas && Array.isArray(params.medidas)
            ? params.medidas
            : [];

        return (
          <div key={m.id || idx} className='ecg-config-escenario-card'>
            <div className='vdr-ecg-config-head'>
              <strong>Escenario {orden}</strong>
              <span className={`ecg-clasificacion-chip ${clasif}`}>
                {clasif || '—'}
              </span>
              <span className='vdr-ecg-config-tipo'>
                {tipo.replace(/_/g, ' ')}
              </span>
            </div>
            {tipo === 'combinacion' && medidas.length > 0 ? (
              <div className='vdr-ecg-config-medidas'>
                {medidas.map((sub, si) => (
                  <ParametrosReadOnly key={si} label={String(sub.tipo || `Medida ${si + 1}`).replace(/_/g, ' ')} config={sub.config || sub} />
                ))}
              </div>
            ) : (
              <ParametrosReadOnly label={tipo.replace(/_/g, ' ')} config={params} />
            )}
          </div>
        );
      })}
    </div>
  );
};

const PARAM_KEY_LABELS: Record<string, string> = {
  tap_sol: 'Tap dentro bloque',
  tap_nosol: 'Tap fuera bloque',
  fp_sol: 'FP bloque',
  fp_nosol: 'FP fuera bloque',
  tipo_fp_sol: 'Naturaleza FP bloque',
  hora_inicio_sol: 'Inicio bloque',
  hora_fin_sol: 'Fin bloque'
};

const PARAM_KEY_ORDER: string[] = [
  'tap_sol', 'tap_nosol',
  'tipo_fp_sol', 'fp_sol', 'fp_nosol',
  'hora_inicio_sol', 'hora_fin_sol',
  'potencia_kw', 'energia_kwh', 'bloque_carga', 'bloque_descarga',
  'potencia_inyeccion_kw'
];

function sortParamEntries(entries: [string, unknown][]): [string, unknown][] {
  return [...entries].sort(([a], [b]) => {
    const ia = PARAM_KEY_ORDER.indexOf(a);
    const ib = PARAM_KEY_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return 0;
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}

const ParametrosReadOnly: FunctionComponent<{ label: string; config: SimpleObject | null | undefined }> = ({ label, config }) => {
  if (!config || typeof config !== 'object') {
    return <p className='ecg-hint'>Sin parámetros detallados para {label}.</p>;
  }

  const entries = sortParamEntries(
    Object.entries(config).filter(([k]) => k !== 'tipo' && k !== 'medidas' && k !== 'config')
  );

  if (entries.length === 0) {
    return <p className='ecg-hint'>Sin parámetros detallados para {label}.</p>;
  }

  return (
    <div className='vdr-ecg-params'>
      <span className='vdr-ecg-params-label'>{label}</span>
      <div className='vdr-ecg-params-list'>
        {entries.map(([k, v]) => (
          <span key={k} className='vdr-ecg-param'>
            <strong>{PARAM_KEY_LABELS[k] ?? k.replace(/_/g, ' ')}:</strong>{' '}
            {typeof v === 'object' ? JSON.stringify(v) : String(v)}
          </span>
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// ─── Bloque 1: Tabla resumen matriz ─────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════

const BloqueTablaResumenMitigacion: FunctionComponent<{
  resultadoTecnicoData: SimpleObject | undefined;
  comparacion: SimpleObject | null;
  mitigaciones: SimpleObject[];
}> = ({ resultadoTecnicoData, comparacion, mitigaciones }) => {
  const mitItems: SimpleObject[] = comparacion?.mitigaciones || [];
  if (mitItems.length === 0 && !comparacion) {
    return <p className='ecg-hint'>Los datos de comparación aún no están disponibles.</p>;
  }

  // NORM-018 A §F.1 — los veredictos SE LEEN DEL MOTOR (no se recalculan a
  // partir de Δ>2 pp). Esto alinea Fase 2 con Fase 1:
  //   • Proyecto sin mitigación ⇒ `resultadoTecnicoData.impacto_proyecto.analisis_*`
  //     (por criterio: OR sobre `tension/corrientes/trafos.tiene_impacto`).
  //   • Cada medida ⇒ `mitItem.analisis_*` (ya lo calcula el motor en
  //     `analizar_impacto_mitigacion` — D-BP-6 / NORM-026). Como atajo
  //     equivalente también leemos `mitItem.restricciones_pendientes`
  //     (derivado del mismo motor) para no duplicar semántica.

  const ipSnap = analisisSnapshotsLista(resultadoTecnicoData?.impacto_proyecto as SimpleObject | undefined);
  const ipDia = analisisDiariosLista(resultadoTecnicoData?.impacto_proyecto as SimpleObject | undefined);

  const ccResumen = parseCortocircuitoResumen(resultadoTecnicoData);
  // Proyecto sin mitigación — veredictos por criterio (no hay impacto ≡ cumple).
  const proyectoNoTieneImpacto = (ti: boolean | null): boolean | null => ti === null ? null : !ti;
  const conProyectoCumple: Record<string, boolean | null> = {
    tension: proyectoNoTieneImpacto(tieneImpactoPorCriterioEnLista(ipSnap, 'tension')),
    corrientes: proyectoNoTieneImpacto(tieneImpactoPorCriterioEnLista(ipSnap, 'corrientes')),
    trafos: proyectoNoTieneImpacto(tieneImpactoPorCriterioEnLista(ipSnap, 'trafos')),
    cortocircuito: ccResumen?.cumple ?? null
  };

  // Mitigaciones — veredicto por criterio desde el motor.
  const mitCumpleMap = mitItems.map((m) => mitigacionCumplePorCriterio(m));

  // NORM-018 A §F.1 — Pérdidas 24×96 (pipeline Art. 3-26) como fila NORMATIVA
  // separada del indicador diario «% tiempo sobre umbral».
  // Si el estudio base indica que pérdidas no aplica (EG), las mitigaciones
  // tampoco la evalúan → N/D en todas las columnas de medidas.
  const perdidasBase = parsePerdidasArtefacto(resultadoTecnicoData);
  const perdidasAplicable = !perdidasBase || perdidasBase.aplicable;
  const perdidas24ConProyecto = perdidasCumpleProyecto(resultadoTecnicoData);
  const perdidas24Mit = mitItems.map((m) => perdidasAplicable ? perdidasCumpleMitigacion(m) : null);

  const filasNormativas: { label: string; key: 'tension' | 'corrientes' | 'trafos' | 'cortocircuito' }[] = [
    { label: 'Snapshots — tensión', key: 'tension' },
    { label: 'Snapshots — líneas', key: 'corrientes' },
    { label: 'Snapshots — transformadores', key: 'trafos' },
    { label: 'Cortocircuito', key: 'cortocircuito' }
  ];

  const filasAdicionales: { label: string; key: 'tension' | 'corrientes' | 'trafos' }[] = [
    { label: 'Flujo horario — tensión (diario)', key: 'tension' },
    { label: 'Flujo horario — líneas (diario)', key: 'corrientes' },
    { label: 'Flujo horario — transformadores (diario)', key: 'trafos' }
  ];

  // Diarios (adicional): proyecto desde `analisis_diarios`; mit desde `mitItem.analisis_diarios`.
  const conDiario = {
    tension: proyectoNoTieneImpacto(tieneImpactoPorCriterioEnLista(ipDia, 'tension')),
    corrientes: proyectoNoTieneImpacto(tieneImpactoPorCriterioEnLista(ipDia, 'corrientes')),
    trafos: proyectoNoTieneImpacto(tieneImpactoPorCriterioEnLista(ipDia, 'trafos'))
  };
  const mitDiarios = mitItems.map((m) => mitigacionCumpleDiarioPorCriterio(m));

  return (
    <div className='vdr-result-table-scroll'>
      <table className='ecg-comparacion-table ecg-tecnico-detalle-table'>
        <thead>
          <tr>
            <th style={{ width: 120 }}>Tipo</th>
            <th>Criterio</th>
            <th>Con proyecto (sin mitigación)</th>
            {mitItems.map((m, i) => (
              <th key={i}>{etiquetaMitigacionFromComparacion(m, mitigaciones, i)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filasNormativas.map(({ label, key }) => (
            <tr key={`norm-${label}`}>
              <td><ChipTipoCriterio tipo='normativo' /></td>
              <td>{label}</td>
              <td><CumpleCellIcon v={conProyectoCumple[key] ?? null} /></td>
              {mitCumpleMap.map((mitCumple, i) => (
                <td key={i}><CumpleCellIcon v={mitCumple[key] ?? null} /></td>
              ))}
            </tr>
          ))}
          <tr>
            <td><ChipTipoCriterio tipo='normativo' /></td>
            <td>Pérdidas — estudio 24×96 (Art. 3-26)</td>
            <td><CumpleCellIcon v={perdidas24ConProyecto} /></td>
            {perdidas24Mit.map((v, i) => (
              <td key={i}><CumpleCellIcon v={v} /></td>
            ))}
          </tr>
          {filasAdicionales.map(({ label, key }) => (
            <tr key={`add-${label}`} style={{ background: 'var(--vdr-soft)' }}>
              <td><ChipTipoCriterio tipo='adicional' /></td>
              <td>{label}</td>
              <td><CumpleCellIcon v={conDiario[key] ?? null} /></td>
              {mitDiarios.map((md, i) => (
                <td key={i}><CumpleCellIcon v={md[key] ?? null} /></td>
              ))}
            </tr>
          ))}
          {/* Pérdidas diarias «% tiempo» se queda en la pestaña «Detalle — flujo
              diario» del desglose (indicador adicional). No duplicamos fila aquí
              para evitar confundir con la normativa 24×96. */}
          <tr style={{ fontWeight: 600 }}>
            <td></td>
            <td>Costo estimado</td>
            <td style={{ color: 'var(--vdr-mute-2)' }}>—</td>
            {mitItems.map((m, i) => (
              <td key={i} style={{ fontVariantNumeric: 'tabular-nums' }}>
                {m.clasificacion === 'operacional'
                  ? 'Sin inversión'
                  : m.costo_estimado_usd != null
                    ? `$${Number(m.costo_estimado_usd).toLocaleString()} USD`
                    : '—'}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
      <p className='ecg-hint' style={{ marginTop: '0.75rem' }}>
        <strong>Normativo:</strong> entra en el veredicto formal / decisión de conexión. <strong>Adicional:</strong>{' '}
        información de valor agregado del producto; no determina por sí sola la decisión.
      </p>
    </div>
  );
};

const ChipTipoCriterio: FunctionComponent<{ tipo: 'normativo' | 'adicional' }> = ({ tipo }) => (
  <span
    className='vdr-result-tag'
    style={{
      fontSize: '11px',
      padding: '2px 7px',
      color: tipo === 'normativo' ? '#0d47a1' : '#b45309',
      background: tipo === 'normativo' ? '#e3f2fd' : '#fff3e0'
    }}>
    {tipo === 'normativo' ? 'Normativo' : 'Adicional'}
  </span>
);

function etiquetaMitigacionFromComparacion(m: SimpleObject, mits: SimpleObject[], idx: number): string {
  const tipo = String(m.tipo || m.tipo_mitigacion || '').replace(/_/g, ' ');
  const matchMit = mits[idx];
  const orden = matchMit?.orden != null ? Number(matchMit.orden) + 1 : idx + 1;
  return `${tipo} (#${orden})`;
}

// ═══════════════════════════════════════════════════════════════════════════
// NORM-018 A / NORM-026 — Veredictos por criterio desde el motor (sin Δ>2pp).
// Equivalencia con Fase 1:
//   • snapshots ⇒ OR(mitItem.analisis_snapshots[].<criterio>.tiene_impacto)
//   • diarios   ⇒ OR(mitItem.analisis_diarios[].<criterio>.tiene_impacto)
// Si alguno de esos campos no está presente (estudio legacy) caemos a
// `restricciones_pendientes` del motor como respaldo.
// ═══════════════════════════════════════════════════════════════════════════

type CriterioNormativo = 'tension' | 'corrientes' | 'trafos';

function mitigacionCumplePorCriterio(
  mitItem: SimpleObject
): Record<'tension' | 'corrientes' | 'trafos' | 'cortocircuito', boolean | null> {
  const snap = analisisSnapshotsLista(mitItem);
  const pendientes = new Set(
    ((mitItem.restricciones_pendientes ?? mitItem.restriccionesPendientes ?? []) as unknown[])
      .map((x) => String(x))
  );

  const porCrit = (c: CriterioNormativo): boolean | null => {
    const ti = tieneImpactoPorCriterioEnLista(snap, c);
    if (ti !== null) return !ti;
    if (pendientes.size > 0) return !pendientes.has(c);
    return null;
  };

  // Cortocircuito: motor no lo incluye en categorías de impacto. Usamos
  // `restricciones_red_global_incumplidas` (legacy fiel: cortocircuito ahí ⇒
  // no cumple). Si no hay info, lo derivamos del detalle_por_estudio.
  const redGlobal = (mitItem.restricciones_red_global_incumplidas
    ?? mitItem.restriccionesRedGlobalIncumplidas ?? []) as unknown[];
  const ccEnRedGlobal = redGlobal.some((x) => String(x).toLowerCase().includes('cortocircuito'));
  let cc: boolean | null = ccEnRedGlobal ? false : null;
  if (cc === null) {
    const det = (mitItem.detalle_por_estudio ?? mitItem.detallePorEstudio) as SimpleObject | undefined;
    if (det && typeof det === 'object') {
      const ccEntry = Object.entries(det).find(([k]) => k.toLowerCase().includes('cortocircuito'));
      const ccObj = ccEntry?.[1] as SimpleObject | undefined;
      if (ccObj && typeof ccObj.cumple === 'boolean') cc = ccObj.cumple;
    }
  }
  if (cc === null) cc = true; // sin señales de incumplimiento CC → asumimos cumple

  return {
    tension: porCrit('tension'),
    corrientes: porCrit('corrientes'),
    trafos: porCrit('trafos'),
    cortocircuito: cc
  };
}

function mitigacionCumpleDiarioPorCriterio(
  mitItem: SimpleObject
): Record<'tension' | 'corrientes' | 'trafos', boolean | null> {
  const dia = analisisDiariosLista(mitItem);
  return {
    tension: invertirOrNull(tieneImpactoPorCriterioEnLista(dia, 'tension')),
    corrientes: invertirOrNull(tieneImpactoPorCriterioEnLista(dia, 'corrientes')),
    trafos: invertirOrNull(tieneImpactoPorCriterioEnLista(dia, 'trafos'))
  };
}

function invertirOrNull(b: boolean | null): boolean | null {
  return b === null ? null : !b;
}

const CumpleCellIcon: FunctionComponent<{ v: boolean | null }> = ({ v }) => {
  if (v === null) return <span className='ecg-hint'>N/D</span>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
      <span
        className={`ecg-semaforo-icon ${v ? 'cumple' : 'no-cumple'}`}
        style={{ display: 'inline-block' }}
      />
      {v ? 'Cumple' : 'No cumple'}
    </span>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// ─── Bloque 2: Desglose por simulación ──────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════

const BloqueDesgloseSimulacion: FunctionComponent<{
  resultadoTecnicoData: SimpleObject;
  comparacion: SimpleObject | null;
  mitigaciones: SimpleObject[];
  tecnicoTab: number;
  setTecnicoTab: (v: number) => void;
  escenarioDiario: string;
  setEscenarioDiario: (v: string) => void;
  escenarioSnapshot: string;
  setEscenarioSnapshot: (v: string) => void;
  selectedMitsDesglose: string[];
  setSelectedMitsDesglose: (v: string[]) => void;
  selectedMitPerdidas: string;
  setSelectedMitPerdidas: (v: string) => void;
}> = ({
  resultadoTecnicoData, comparacion, mitigaciones,
  tecnicoTab, setTecnicoTab,
  escenarioDiario, setEscenarioDiario,
  escenarioSnapshot, setEscenarioSnapshot,
  selectedMitsDesglose, setSelectedMitsDesglose,
  selectedMitPerdidas, setSelectedMitPerdidas
}) => {
  const toggleMit = (mitId: string) => {
    setSelectedMitsDesglose(
      selectedMitsDesglose.includes(mitId)
        ? selectedMitsDesglose.filter((x) => x !== mitId)
        : [...selectedMitsDesglose, mitId]
    );
  };

  // Default seleccionada para tab Pérdidas
  const defaultMitPerdidasId = useMemo(
    () => (mitigaciones[0]?.id != null ? String(mitigaciones[0].id) : ''),
    [mitigaciones]
  );
  const mitPerdidasIdEfectivo = selectedMitPerdidas || defaultMitPerdidasId;

  const esTabSnapshot = tecnicoTab === 0;
  const esTabPerdidas = tecnicoTab === 1;
  const esTabDiario = tecnicoTab === 2;

  return (
    <div>
      {/* Selector de mitigaciones a mostrar (solo relevante en tablas comparativas snapshot / diario) */}
      {mitigaciones.length > 0 && !esTabPerdidas && (
        <div className='vdr-result-mit-toggles'>
          <span className='ecg-label'>
            Mitigaciones visibles en tabla:
          </span>
          <div className='vdr-result-mit-toggles-list'>
            {mitigaciones.map((m) => (
              <FormControlLabel
                key={m.id}
                control={
                  <Checkbox
                    size='small'
                    checked={selectedMitsDesglose.includes(String(m.id))}
                    onChange={() => toggleMit(String(m.id))}
                  />
                }
                label={etiquetaMitigacion(m)}
                sx={{ '& .MuiTypography-root': { fontSize: '0.9rem', fontWeight: 600, color: 'var(--vdr-text)' } }}
              />
            ))}
          </div>
        </div>
      )}

      <Tabs value={tecnicoTab} onChange={(_, v) => setTecnicoTab(v)} sx={{ mb: 2 }}>
        <Tab label='Detalle — snapshots' />
        <Tab label='Detalle — pérdidas' />
        <Tab label={
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            Detalle — flujo diario<BadgeAdicional small />
          </span>
        } />
      </Tabs>

      {esTabSnapshot && (
        <TablaDesgloseConMitigaciones
          tipo='snapshot'
          resultadoTecnicoData={resultadoTecnicoData}
          comparacion={comparacion}
          mitigaciones={mitigaciones}
          selectedMitIds={selectedMitsDesglose}
          escenarioSeleccionado={escenarioSnapshot}
          onCambioEscenario={setEscenarioSnapshot}
        />
      )}

      {esTabPerdidas && (
        <BloqueDetallePerdidasMitigacion
          resultadoTecnicoData={resultadoTecnicoData}
          comparacion={comparacion}
          mitigaciones={mitigaciones}
          selectedMitId={mitPerdidasIdEfectivo}
          onChangeMitigacion={setSelectedMitPerdidas}
        />
      )}

      {esTabDiario && (
        <div>
          <p className='ecg-hint' style={{ marginBottom: '0.75rem' }}>
            <strong>Información adicional.</strong> El flujo diario es análisis complementario;
            no determina por sí solo la decisión de conexión con mitigación.
          </p>
          <TablaDesgloseConMitigaciones
            tipo='diario'
            resultadoTecnicoData={resultadoTecnicoData}
            comparacion={comparacion}
            mitigaciones={mitigaciones}
            selectedMitIds={selectedMitsDesglose}
            escenarioSeleccionado={escenarioDiario}
            onCambioEscenario={setEscenarioDiario}
          />
        </div>
      )}
    </div>
  );
};

// ─── Tabla desglose con columnas de mitigación ──────────────────────────
//
// NORM-018 A / NORM-026 — Para alinear con Fase 1, el veredicto «Cumple» de
// cada columna se toma del MOTOR (no se recalcula con Δ>2 pp):
//   • «Con proyecto (sin mitigación)» ⇒ `impacto_proyecto.analisis_<tipo>[esc].<criterio>.tiene_impacto`
//   • «Medida M» ⇒ `mitItem.analisis_<tipo>[esc].<criterio>.tiene_impacto`
// Los Δ pp se colorean por SIGNO (rojo si empeora, verde si mejora o igual),
// no comparando contra umbral 2 pp.

type FilaMitDesglose = {
  criterio: string;
  critMotor: 'tension' | 'corrientes' | 'trafos' | null;
  sinProyecto: string;
  conProyecto: string;
  deltaVsBase: string;
  cumpleConProyecto: boolean | null;
  mitData: {
    valor: string;
    deltaVsBase: string;
    deltaVsConProyecto: string;
    cumple: boolean | null;
  }[];
};

const TablaDesgloseConMitigaciones: FunctionComponent<{
  tipo: 'diario' | 'snapshot';
  resultadoTecnicoData: SimpleObject;
  comparacion: SimpleObject | null;
  mitigaciones: SimpleObject[];
  selectedMitIds: string[];
  escenarioSeleccionado: string;
  onCambioEscenario: (v: string) => void;
}> = ({
  tipo, resultadoTecnicoData, comparacion, mitigaciones, selectedMitIds,
  escenarioSeleccionado, onCambioEscenario
}) => {
  const rt = parseResultadoTecnico(resultadoTecnicoData);
  const opts = tipo === 'diario'
    ? escenariosDiariosConProyecto(rt)
    : escenariosSnapshotConProyecto(rt);

  const mitItems: SimpleObject[] = useMemo(
    () => comparacion?.mitigaciones || [],
    [comparacion]
  );
  const selectedMits = useMemo(
    () => mitigaciones.filter((m) => selectedMitIds.includes(String(m.id))),
    [mitigaciones, selectedMitIds]
  );

  const filas = useMemo(
    () => buildFilasMitDesglose(tipo, rt, escenarioSeleccionado, resultadoTecnicoData, mitItems, selectedMits),
    [tipo, rt, escenarioSeleccionado, resultadoTecnicoData, mitItems, selectedMits]
  );

  const esDiario = tipo === 'diario';

  // ΔV PC — una fila por caso visible: «Con proyecto (sin mitigación)» + una
  // por cada medida seleccionada. Análisis del motor por escenario (D3 Opción A).
  const listaDeltaAnalisis = tipo === 'diario'
    ? analisisDiariosLista(resultadoTecnicoData?.impacto_proyecto as SimpleObject | undefined)
    : analisisSnapshotsLista(resultadoTecnicoData?.impacto_proyecto as SimpleObject | undefined);
  const analisisConProyecto = analisisPorEscenario(listaDeltaAnalisis, escenarioSeleccionado);
  const filasDeltaVPc = [
    { titulo: 'Con proyecto (sin mitigación)', analisis: analisisConProyecto },
    ...selectedMits.map((m, idx) => {
      const mitItem = findMitItemByMit(m, mitItems, idx);
      const mitLista = tipo === 'diario' ? analisisDiariosLista(mitItem) : analisisSnapshotsLista(mitItem);
      return {
        titulo: etiquetaMitigacion(m),
        analisis: analisisPorEscenario(mitLista, escenarioSeleccionado)
      };
    })
  ];

  // Congestión en transmisión — solo en pestaña snapshot. La fila «Con proyecto»
  // viene del JSON Fase 1 (resultadoTecnicoData.congestion_zonal); cada medida
  // visible aporta su propio bloque desde comparacion.mitigaciones[i].
  const congestionConProyecto = (resultadoTecnicoData?.congestion_zonal
    ?? resultadoTecnicoData?.congestionZonal) as SimpleObject | undefined;
  const filasCongestion = [
    { titulo: 'Con proyecto (sin mitigación)', congestion: congestionConProyecto },
    ...selectedMits.map((m, idx) => {
      const mitItem = findMitItemByMit(m, mitItems, idx);
      const congestion = (mitItem?.congestion_zonal ?? mitItem?.congestionZonal) as
        | SimpleObject
        | undefined;
      return { titulo: etiquetaMitigacion(m), congestion };
    })
  ];

  return (
    <div>
      <div className='ecg-form-row'>
        <div className='ecg-form-field' style={{ maxWidth: 420 }}>
          <label className='ecg-label'>
            {esDiario ? 'Escenario de estudio diario' : 'Escenario snapshot'}
          </label>
          <select
            className='ecg-input'
            value={escenarioSeleccionado}
            onChange={(e) => onCambioEscenario(e.target.value)}>
            {opts.length === 0 && <option value=''>—</option>}
            {opts.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {!escenarioSeleccionado && <p className='ecg-hint'>Seleccione un escenario.</p>}

      {escenarioSeleccionado && filas.length > 0 && (
        <div className='vdr-result-table-scroll' style={{ marginTop: '0.75rem' }}>
          <p className='ecg-hint' style={{ marginBottom: '0.5rem' }}>
            «Sin empeoramiento» refleja el veredicto de la simulación por criterio. La columna de variación
            se colorea por signo: rojo si empeora, verde si mejora o igual.
          </p>
          <table className='ecg-comparacion-table ecg-tecnico-detalle-table'>
            <thead>
              <tr>
                <th>Criterio</th>
                <th className='vdr-result-num'>Sin proyecto</th>
                <th className='vdr-result-num'>Con proyecto</th>
                <th className='vdr-result-num'>Variación vs base (pp)</th>
                <th>Sin empeoramiento</th>
                {selectedMits.map((m) => (
                  [
                    <th key={`${m.id}-val`} className='vdr-result-num'>{etiquetaMitigacion(m)} (%)</th>,
                    <th key={`${m.id}-db`} className='vdr-result-num'>{etiquetaMitigacion(m)} Var. vs base</th>,
                    <th key={`${m.id}-dp`} className='vdr-result-num'>{etiquetaMitigacion(m)} Var. vs proyecto</th>,
                    <th key={`${m.id}-c`}>{etiquetaMitigacion(m)} Sin empeoramiento</th>
                  ]
                )).flat()}
              </tr>
            </thead>
            <tbody>
              {filas.map((f) => (
                <tr key={f.criterio}>
                  <td>{f.criterio}</td>
                  <td className='vdr-result-num'>{f.sinProyecto}</td>
                  <td className='vdr-result-num'>{f.conProyecto}</td>
                  <td className={`vdr-result-num ${celdaDeltaSignoClassExport(f.deltaVsBase)}`}>{f.deltaVsBase}</td>
                  <td><CumpleCellIcon v={f.cumpleConProyecto} /></td>
                  {f.mitData.map((md, mi) => (
                    [
                      <td key={`${mi}-v`} className='vdr-result-num'>{md.valor}</td>,
                      <td key={`${mi}-db`} className={`vdr-result-num ${celdaDeltaSignoClassExport(md.deltaVsBase)}`}>{md.deltaVsBase}</td>,
                      <td key={`${mi}-dp`} className={`vdr-result-num ${celdaDeltaSignoClassExport(md.deltaVsConProyecto)}`}>{md.deltaVsConProyecto}</td>,
                      <td key={`${mi}-c`}><CumpleCellIcon v={md.cumple} /></td>
                    ]
                  )).flat()}
                </tr>
              ))}
              {filas.length === 0 && (
                <tr>
                  <td colSpan={5 + selectedMits.length * 4} style={{ textAlign: 'center' }}>
                    <span className='ecg-hint'>Sin datos para este escenario.</span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* NORM-018 A — ΔV en punto de conexión (NORM-013/014): otra dimensión
              que entra al veredicto de impacto, mostrada igual que en Fase 1. */}
          <BloqueDeltaVPcMulti contexto={tipo} filas={filasDeltaVPc} />

          {/* Congestión en transmisión (NORM-028 — etiqueta interna). La tabla
              es por escenario seleccionado y solo aplica en escenarios genmax;
              el componente decide internamente si renderizar o no. Se muestra
              sólo en la pestaña de snapshots. */}
          {tipo === 'snapshot' && (
            <BloqueCongestionTransmisionMulti
              escenarioSeleccionado={escenarioSeleccionado}
              filas={filasCongestion}
            />
          )}
        </div>
      )}
    </div>
  );
};

function buildFilasMitDesglose(
  tipo: 'diario' | 'snapshot',
  rt: ReturnType<typeof parseResultadoTecnico>,
  escenario: string,
  resultadoTecnicoData: SimpleObject,
  mitItems: SimpleObject[],
  selectedMits: SimpleObject[]
): FilaMitDesglose[] {
  if (!escenario) return [];

  const esDiario = tipo === 'diario';
  // Mismos labels y campos que Fase 1 («% elementos», no «% tiempo»).
  const criterios: { key: string; label: string; critMotor: 'tension' | 'corrientes' | 'trafos' | null }[] = esDiario
    ? [
        { key: 'pct_buses_con_problema', label: 'Tensión (% buses fuera de banda)', critMotor: 'tension' },
        { key: 'pct_lineas_con_problema', label: 'Líneas — corrientes (% líneas en sobrecarga)', critMotor: 'corrientes' },
        { key: 'pct_trafos_con_problema', label: 'Transformadores (% trafos en sobrecarga)', critMotor: 'trafos' },
        { key: 'pct_tiempo_perdidas_sobre_umbral', label: 'Pérdidas (% tiempo sobre umbral)', critMotor: null }
      ]
    : [
        { key: 'pct_buses_fuera_banda', label: 'Tensión (% buses fuera de banda)', critMotor: 'tension' },
        { key: 'pct_lineas_sobrecarga', label: 'Líneas — corrientes (% líneas en sobrecarga)', critMotor: 'corrientes' },
        { key: 'pct_trafos_sobrecarga', label: 'Transformadores (% trafos en sobrecarga)', critMotor: 'trafos' }
      ];

  const conRow = (esDiario ? rt.estudiosDiarios : rt.estudiosSnapshots)
    .find((r) => String(r.escenario) === escenario);

  let baseRow: SimpleObject | undefined;
  if (esDiario) {
    baseRow = rt.estudiosDiariosBase.find((r) => String(r.escenario) === escenario);
  } else {
    const escBase = escenario.replace('genmax', 'sinGD');
    baseRow = rt.estudiosSnapshotsBase.find((r) => String(r.escenario) === escBase);
  }

  // Análisis por escenario desde el motor (proyecto).
  const listaProy = esDiario
    ? analisisDiariosLista(resultadoTecnicoData?.impacto_proyecto as SimpleObject | undefined)
    : analisisSnapshotsLista(resultadoTecnicoData?.impacto_proyecto as SimpleObject | undefined);
  const analisisProyectoEsc = analisisPorEscenario(listaProy, escenario);

  return criterios.map(({ key, label, critMotor }) => {
    const sinP = baseRow ? numO(baseRow[key]) : null;
    const conP = conRow ? numO(conRow[key]) : null;
    const delta = sinP !== null && conP !== null ? round2(conP - sinP) : null;

    // Cumple del proyecto ⇒ motor `tiene_impacto` invertido, o, para pérdidas
    // diarias (sin criterio motor equivalente), derivamos localmente por signo
    // (Δ ≤ 0 ⇒ sin empeoramiento) como en Fase 1.
    let cumpleConProyecto: boolean | null = null;
    if (critMotor) {
      const sub = analisisProyectoEsc?.[critMotor] as SimpleObject | undefined;
      const ti = sub?.tiene_impacto ?? sub?.tieneImpacto;
      cumpleConProyecto = typeof ti === 'boolean' ? !ti : null;
    } else if (delta !== null) {
      cumpleConProyecto = delta <= 0;
    }

    const mitData = selectedMits.map((m) => {
      const mitIdx = selectedMits.indexOf(m);
      const mitItem = findMitItemByMit(m, mitItems, mitIdx);
      const mitDetalle = mitItem?.detalle_por_estudio || mitItem?.detallePorEstudio;
      let mitVal: number | null = null;

      if (mitDetalle && typeof mitDetalle === 'object') {
        for (const [detKey, detVal] of Object.entries(mitDetalle)) {
          if (typeof detVal === 'object' && detVal !== null) {
            const escMatch = escenario.includes(detKey) || detKey.includes(escenario.split('__')[0]);
            if (escMatch) {
              mitVal = numO((detVal as SimpleObject)[key]);
              if (mitVal !== null) break;
            }
          }
        }
        if (mitVal === null) {
          const flatVal = numO((mitDetalle as SimpleObject)[key]);
          if (flatVal !== null) mitVal = flatVal;
        }
      }

      const dVsBase = sinP !== null && mitVal !== null ? round2(mitVal - sinP) : null;
      const dVsProyecto = conP !== null && mitVal !== null ? round2(mitVal - conP) : null;

      // Cumple de la medida ⇒ motor `tiene_impacto` en su análisis por escenario.
      let cumpleMit: boolean | null = null;
      if (critMotor) {
        const mitLista = esDiario ? analisisDiariosLista(mitItem) : analisisSnapshotsLista(mitItem);
        const analisisMitEsc = analisisPorEscenario(mitLista, escenario);
        const sub = analisisMitEsc?.[critMotor] as SimpleObject | undefined;
        const ti = sub?.tiene_impacto ?? sub?.tieneImpacto;
        cumpleMit = typeof ti === 'boolean' ? !ti : null;
      } else if (dVsBase !== null) {
        cumpleMit = dVsBase <= 0;
      }

      return {
        valor: fmtPct(mitVal),
        deltaVsBase: dVsBase === null ? '—' : `${dVsBase.toFixed(2)} pp`,
        deltaVsConProyecto: dVsProyecto === null ? '—' : `${dVsProyecto.toFixed(2)} pp`,
        cumple: cumpleMit
      };
    });

    return {
      criterio: label,
      critMotor,
      sinProyecto: fmtPct(sinP),
      conProyecto: fmtPct(conP),
      deltaVsBase: delta === null ? '—' : `${delta.toFixed(2)} pp`,
      cumpleConProyecto,
      mitData
    };
  });
}

function findMitItemByMit(m: SimpleObject, mitItems: SimpleObject[], _idx: number): SimpleObject | undefined {
  const mitId = String(m.id);
  const found = mitItems.find((item) => {
    const itemMitRef = item.mitigacion_ref || item.mitigacionRef;
    if (itemMitRef && String(itemMitRef) === mitId) return true;
    return false;
  });
  if (found) return found;
  const orden = m.orden != null ? Number(m.orden) : _idx;
  return mitItems[orden] ?? mitItems[_idx];
}

// ═══════════════════════════════════════════════════════════════════════════
// ─── Bloque 3: Análisis gráfico comparativo lado a lado ─────────────────
// ═══════════════════════════════════════════════════════════════════════════

type CasoGrafico = { tipo: 'sin_proyecto' | 'con_proyecto' | 'mitigacion'; mitigacionId?: string };

type SimulacionDef = {
  id: string;
  label: string;
  tipo: 'diario' | 'snapshot';
  sinProyecto: string;
  conProyecto: string;
  mitigacion: string;
};

// NORM-002 / PLAN_NORM_002_SNAPSHOTS_DEMANDA_ANUAL §8.5: 4 snapshots por
// estudio con sufijo `_anual` (sin duplicación verano/invierno). Los diarios
// 1440 mantienen la separación verano/invierno (fuera de alcance NORM-002).
const SIMULACIONES_GRAFICO: SimulacionDef[] = [
  { id: 'dia_verano', label: 'Día verano', tipo: 'diario',
    sinProyecto: 'dia_verano__sin_proyecto', conProyecto: 'dia_verano__con_proyecto', mitigacion: 'dia_verano' },
  { id: 'dia_invierno', label: 'Día invierno', tipo: 'diario',
    sinProyecto: 'dia_invierno__sin_proyecto', conProyecto: 'dia_invierno__con_proyecto', mitigacion: 'dia_invierno' },
  { id: 'snap_dmin_anual', label: 'Snapshot Dmin (anual)', tipo: 'snapshot',
    sinProyecto: 'snapshot_dmin_sinGD_anual__sin_proyecto', conProyecto: 'snapshot_dmin_genmax_anual__con_proyecto', mitigacion: 'snapshot_dmin_genmax_anual' },
  { id: 'snap_dmax_anual', label: 'Snapshot Dmax (anual)', tipo: 'snapshot',
    sinProyecto: 'snapshot_dmax_sinGD_anual__sin_proyecto', conProyecto: 'snapshot_dmax_genmax_anual__con_proyecto', mitigacion: 'snapshot_dmax_genmax_anual' }
];

function buildFetchParams(
  sim: SimulacionDef,
  caso: CasoGrafico
): { escenario: string; mitigacionId?: string } | null {
  if (caso.tipo === 'sin_proyecto') return { escenario: sim.sinProyecto };
  if (caso.tipo === 'con_proyecto') return { escenario: sim.conProyecto };
  if (caso.tipo === 'mitigacion' && caso.mitigacionId) {
    return { escenario: sim.mitigacion, mitigacionId: caso.mitigacionId };
  }
  return null;
}

function casoLabel(caso: CasoGrafico, mitigaciones: SimpleObject[]): string {
  if (caso.tipo === 'sin_proyecto') return 'Sin proyecto';
  if (caso.tipo === 'con_proyecto') return 'Con proyecto (sin mitigación)';
  const mit = mitigaciones.find((m) => String(m.id) === caso.mitigacionId);
  return mit ? etiquetaMitigacion(mit) : 'Mitigación';
}

function buildCasoOptions(mitigaciones: SimpleObject[]): { value: string; label: string }[] {
  const opts = [
    { value: 'sin_proyecto', label: 'Sin proyecto' },
    { value: 'con_proyecto', label: 'Con proyecto (sin mitigación)' }
  ];
  for (const m of mitigaciones) {
    opts.push({ value: `mit_${m.id}`, label: etiquetaMitigacion(m) });
  }
  return opts;
}

function parseCasoValue(val: string): CasoGrafico {
  if (val === 'sin_proyecto') return { tipo: 'sin_proyecto' };
  if (val === 'con_proyecto') return { tipo: 'con_proyecto' };
  if (val.startsWith('mit_')) return { tipo: 'mitigacion', mitigacionId: val.slice(4) };
  return { tipo: 'con_proyecto' };
}

function casoToValue(caso: CasoGrafico): string {
  if (caso.tipo === 'sin_proyecto') return 'sin_proyecto';
  if (caso.tipo === 'con_proyecto') return 'con_proyecto';
  return `mit_${caso.mitigacionId || ''}`;
}

const ECG_DECILE_LINE_COLORS = [
  '#d0d0d0', // p0
  '#b0b0b0', // p2
  '#909090', // p5
  '#1b9e77', // p10
  '#d95f02', // p20
  '#7570b3', // p30
  '#e7298a', // p40
  '#66a61e', // p50
  '#e6ab02', // p60
  '#a6761d', // p70
  '#666666', // p80
  '#01665e', // p90
  '#909090', // p95
  '#b0b0b0', // p98
  '#d0d0d0'  // p100
];

type EcgFlujoSubVista = 'tension' | 'corriente' | 'trafos';

function ecgPickDj<T>(dj: SimpleObject, snake: string, camel: string): T | undefined {
  const a = dj[snake];
  if (a !== undefined && a !== null) return a as T;
  const b = dj[camel];
  if (b !== undefined && b !== null) return b as T;
  return undefined;
}

function ecgInstantesMuestra(dj: SimpleObject): number[] | null {
  const raw = ecgPickDj<unknown>(dj, 'instantes_muestra', 'instantesMuestra');
  if (!Array.isArray(raw) || raw.length === 0) return null;
  return raw.map((x) => Number(x));
}

function ecgIsLegacyDetalleCharts(dj: SimpleObject): boolean {
  return Array.isArray(dj.timestamps) && dj.values != null && typeof dj.values === 'object' && !Array.isArray(dj.values);
}

function ecgDecileSeries(bundle: unknown): { label: string; data: number[]; curve: 'linear' }[] {
  if (!bundle || typeof bundle !== 'object') return [];
  const o = bundle as SimpleObject;
  const order = ['p0', 'p2', 'p5', 'p10', 'p20', 'p30', 'p40', 'p50', 'p60', 'p70', 'p80', 'p90', 'p95', 'p98', 'p100'];
  const out: { label: string; data: number[]; curve: 'linear' }[] = [];
  for (const k of order) {
    const arr = o[k];
    if (Array.isArray(arr)) {
      out.push({ label: k.toUpperCase(), data: arr.map((x) => Number(x)), curve: 'linear' });
    }
  }
  return out;
}

function ecgFlujoDecilesBundle(dj: SimpleObject, sub: EcgFlujoSubVista): unknown {
  if (sub === 'tension') return ecgPickDj(dj, 'tensiones_bus_pu_deciles', 'tensionesBusPuDeciles');
  if (sub === 'corriente') return ecgPickDj(dj, 'corrientes_linea_pu_deciles', 'corrientesLineaPuDeciles');
  return ecgPickDj(dj, 'utilizacion_trafos_pu_deciles', 'utilizacionTrafosPuDeciles');
}

function ecgMinutesToHHmm(minutesFromMidnight: number): string {
  let m = Math.round(minutesFromMidnight) % (24 * 60);
  if (m < 0) m += 24 * 60;
  const h = Math.floor(m / 60);
  const min = m % 60;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

function ecgTickIntervalEvery30Min(minutesSeries: number[]) {
  const lastIdx = minutesSeries.length - 1;
  return (value: unknown, index: number) => {
    const mv = Number(value);
    if (!Number.isFinite(mv)) return false;
    if (index === lastIdx) return true;
    return mv % 30 === 0;
  };
}

function ecgTimeOfDayXAxisConfig(xData: number[], dj: SimpleObject) {
  const step = ecgPickDj<number>(dj, 'intervalo_minutos', 'intervaloMinutos');
  const stepLabel =
    step != null && !Number.isNaN(step)
      ? `Hora del día (cada ${step} min)`
      : 'Hora del día';
  return {
    scaleType: 'point' as const,
    data: xData,
    label: stepLabel,
    valueFormatter: (value: number) => ecgMinutesToHHmm(Number(value)),
    tickInterval: ecgTickIntervalEvery30Min(xData)
  };
}

// ─── Componente orquestador: selectores + dos paneles ────────────────────

const BloqueGraficoComparativo: FunctionComponent<{
  mitigaciones: SimpleObject[];
  simulacion: string;
  setSimulacion: (v: string) => void;
  casoIzq: CasoGrafico;
  setCasoIzq: (v: CasoGrafico) => void;
  casoDer: CasoGrafico;
  setCasoDer: (v: CasoGrafico) => void;
  detalleIzq: SimpleObject[] | null;
  detalleDer: SimpleObject[] | null;
  bandasTension: EcgBandasTension;
}> = ({
  mitigaciones, simulacion, setSimulacion,
  casoIzq, setCasoIzq, casoDer, setCasoDer,
  detalleIzq, detalleDer, bandasTension
}) => {
  const [flujoSubVista, setFlujoSubVista] = useState<EcgFlujoSubVista>('tension');
  const casoOpts = buildCasoOptions(mitigaciones);
  const sim = SIMULACIONES_GRAFICO.find((s) => s.id === simulacion);
  const esDiario = sim?.tipo === 'diario';

  return (
    <div>
      {/* Fila de selectores */}
      <div className='ecg-form-row' style={{ marginBottom: '1rem' }}>
        <div className='ecg-form-field' style={{ maxWidth: 300 }}>
          <label className='ecg-label'>Simulación</label>
          <select className='ecg-input' value={simulacion} onChange={(e) => setSimulacion(e.target.value)}>
            <option value=''>Seleccione una simulación</option>
            {SIMULACIONES_GRAFICO.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </div>
        <div className='ecg-form-field' style={{ maxWidth: 300 }}>
          <label className='ecg-label'>Panel izquierdo</label>
          <select
            className='ecg-input'
            value={casoToValue(casoIzq)}
            onChange={(e) => setCasoIzq(parseCasoValue(e.target.value))}>
            {casoOpts.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className='ecg-form-field' style={{ maxWidth: 300 }}>
          <label className='ecg-label'>Panel derecho</label>
          <select
            className='ecg-input'
            value={casoToValue(casoDer)}
            onChange={(e) => setCasoDer(parseCasoValue(e.target.value))}>
            {casoOpts.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Selector de magnitud (compartido, solo para flujo diario) */}
      {simulacion && esDiario && (
        <div className='ecg-form-row' style={{ marginBottom: '1rem' }}>
          <div className='ecg-form-field' style={{ maxWidth: 320 }}>
            <label className='ecg-label'>Magnitud (flujo diario)</label>
            <select
              className='ecg-input'
              value={flujoSubVista}
              onChange={(e) => setFlujoSubVista(e.target.value as EcgFlujoSubVista)}>
              <option value='tension'>Tensión en buses (deciles)</option>
              <option value='corriente'>Corriente en líneas (deciles)</option>
              <option value='trafos'>Utilización transformadores (deciles)</option>
            </select>
          </div>
        </div>
      )}

      {!simulacion && (
        <p className='ecg-hint' style={{ marginTop: '0.5rem' }}>Seleccione una simulación para ver los gráficos.</p>
      )}

      {/* Dos paneles lado a lado */}
      {simulacion && (
        <div className='vdr-result-panels'>
          <PanelGrafico
            titulo={casoLabel(casoIzq, mitigaciones)}
            caso={casoIzq}
            detalleData={detalleIzq}
            flujoSubVista={flujoSubVista}
            bandasTension={bandasTension}
          />
          <PanelGrafico
            titulo={casoLabel(casoDer, mitigaciones)}
            caso={casoDer}
            detalleData={detalleDer}
            flujoSubVista={flujoSubVista}
            bandasTension={bandasTension}
          />
        </div>
      )}
    </div>
  );
};

// ─── Panel gráfico reutilizable (un caso) ────────────────────────────────

const PanelGrafico: FunctionComponent<{
  titulo: string;
  caso: CasoGrafico;
  detalleData: SimpleObject[] | null;
  flujoSubVista: EcgFlujoSubVista;
  bandasTension: EcgBandasTension;
}> = ({ titulo, caso, detalleData, flujoSubVista, bandasTension }) => {
  if (!detalleData) {
    return (
      <div className='vdr-result-panel'>
        <h4 className='vdr-result-panel-title'>{titulo}</h4>
        <CircularProgress size={28} />
      </div>
    );
  }

  if (detalleData.length === 0) {
    return (
      <div className='vdr-result-panel'>
        <h4 className='vdr-result-panel-title'>{titulo}</h4>
        <p className='ecg-hint'>No hay datos disponibles para este caso.</p>
      </div>
    );
  }

  return (
    <div className='vdr-result-panel'>
      <h4 className='vdr-result-panel-title'>{titulo}</h4>
      {detalleData.map((d, i) => {
        const dj = d.datosJson as SimpleObject | undefined;
        if (!dj || typeof dj !== 'object') return null;
        const tipo = String(d.tipoResultado || '');

        if (tipo === 'flujo_snapshot') {
          const bp = ecgExtractBoxplotSnapshot(dj);
          // En la página de mitigaciones cada panel representa un caso ya
          // elegido por el usuario; la comparación la hacen los dos paneles
          // entre sí. Por eso el boxplot aquí va en modo `unico` (una sola
          // caja) y no en modo comparativo (sin/con proyecto), que sí se usa
          // en resultados de conexión (Fase 1).
          const ramaUnica: 'sin_proyecto' | 'con_proyecto' =
            caso.tipo === 'sin_proyecto' ? 'sin_proyecto' : 'con_proyecto';
          return (
            <div key={i} style={{ marginBottom: '1rem' }}>
              {bp ? (
                <EstudioConexionSnapshotBoxplots
                  boxplotRoot={bp}
                  modo='unico'
                  ramaUnica={ramaUnica}
                  etiquetaUnica={titulo}
                  bandas={bandasTension}
                />
              ) : (
                <p className='ecg-hint'>Boxplots no disponibles.</p>
              )}
            </div>
          );
        }

        if (ecgIsLegacyDetalleCharts(dj)) {
          const series = Object.keys(dj.values as SimpleObject).map((k) => ({
            label: k.replace(/_/g, ' '),
            curve: 'linear' as const,
            data: (dj.values as SimpleObject)[k] as number[]
          }));
          return (
            <div key={i} style={{ marginBottom: '1rem' }}>
              <LineChart
                height={300}
                xAxis={[{ data: dj.timestamps as number[], label: 'Tiempo' }]}
                yAxis={[{ label: tipo }]}
                series={series}
              />
            </div>
          );
        }

        const xData = ecgInstantesMuestra(dj);
        const xAxisCfg = xData != null ? ecgTimeOfDayXAxisConfig(xData, dj) : null;

        if (tipo === 'flujo_dia' && xData) {
          if (flujoSubVista === 'tension') {
            return (
              <div key={i} style={{ marginBottom: '1rem' }}>
                <EstudioConexionTensionDiariaChart
                  datosJson={dj}
                  bandas={bandasTension}
                  height={320}
                />
              </div>
            );
          }
          const bundle = ecgFlujoDecilesBundle(dj, flujoSubVista);
          const series = ecgDecileSeries(bundle);
          const yLabel =
            flujoSubVista === 'corriente' ? 'Corriente (pu)' : 'Utilización trafos (pu)';
          return (
            <div key={i} style={{ marginBottom: '1rem' }}>
              {series.length === 0 ? (
                <p className='ecg-hint'>Sin series de deciles.</p>
              ) : (
                <LineChart
                  height={320}
                  xAxis={xAxisCfg ? [xAxisCfg] : []}
                  yAxis={[{ label: yLabel }]}
                  series={series}
                  colors={ECG_DECILE_LINE_COLORS}
                />
              )}
            </div>
          );
        }

        if (!xData) return null;

        if (tipo === 'perdidas') {
          const kw = ecgPickDj<number[]>(dj, 'losses_kW', 'lossesKW');
          const pct = ecgPickDj<number[]>(dj, 'losses_pct', 'lossesPct');
          if (!kw || !pct) return null;
          return (
            <div key={i} style={{ marginBottom: '1rem' }}>
              <span className='vdr-result-chart-title'>Pérdidas</span>
              <LineChart
                height={280}
                xAxis={xAxisCfg ? [xAxisCfg] : []}
                yAxis={[
                  { id: 'kw', label: 'kW' },
                  { id: 'pct', label: '%' }
                ]}
                series={[
                  { curve: 'linear', data: kw, label: 'kW', yAxisId: 'kw' },
                  { curve: 'linear', data: pct, label: '%', yAxisId: 'pct' }
                ]}
              />
            </div>
          );
        }

        if (tipo === 'potencia_gd') {
          const pg = ecgPickDj<number[]>(dj, 'potencia_gd_kw', 'potenciaGdKw');
          const pc = ecgPickDj<number[]>(dj, 'potencia_cabecera_kw', 'potenciaCabeceraKw');
          if (!pg || !pc) return null;
          return (
            <div key={i} style={{ marginBottom: '1rem' }}>
              <span className='vdr-result-chart-title'>Potencia</span>
              <LineChart
                height={280}
                xAxis={xAxisCfg ? [xAxisCfg] : []}
                yAxis={[{ label: 'kW' }]}
                series={[
                  { curve: 'linear', data: pg, label: 'GD (kW)' },
                  { curve: 'linear', data: pc, label: 'Cabecera (kW)' }
                ]}
              />
            </div>
          );
        }

        if (tipo === 'bateria') {
          const pbat = ecgPickDj<number[]>(dj, 'potencia_kw', 'potenciaKw');
          const soc = ecgPickDj<number[]>(dj, 'soc_pct', 'socPct');
          if (!pbat || !soc) return null;
          return (
            <div key={i} style={{ marginBottom: '1rem' }}>
              <span className='vdr-result-chart-title'>Batería</span>
              <LineChart
                height={280}
                xAxis={xAxisCfg ? [xAxisCfg] : []}
                yAxis={[
                  { id: 'p', label: 'kW' },
                  { id: 'soc', label: 'SoC (%)' }
                ]}
                series={[
                  { curve: 'linear', data: pbat, label: 'kW', yAxisId: 'p' },
                  { curve: 'linear', data: soc, label: 'SoC %', yAxisId: 'soc' }
                ]}
              />
            </div>
          );
        }

        return null;
      })}
    </div>
  );
};
